import React, { useState, useEffect, useRef, memo } from 'react';
import clsx from 'clsx';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import './react-datepicker_changes.css';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { parse, isValid, format, getDate, isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import Icon from '@mdi/react';
import { mdiCalendarBlank } from '@mdi/js';
import { getDefaultLocale } from "react-datepicker";
import dayjs from 'dayjs';
import moment from 'moment';
import TextInput from 'components/TextInput/TextInput';
import { IconButton } from '@material-ui/core';


const useStyles = makeStyles((theme) => ({
    icon: {
        position: "absolute",
        right: "1px",
        top: "50%",
        transform: "translateY(-50%)",
        opacity: (props) => (props.disabled ? 0.4 : 1)
    },
    '@global': {
        '.react-datepicker__input-container': { // Style for the input container
            paddingLeft: '2px',
        },
        '.react-datepicker-popper': { // Style for the popper
            zIndex: '999 !important'
        },
        '.react-datepicker-wrapper': { // Style for the wrapper
            width: '100% !important',
        },
        '.react-datepicker__view-calendar-icon input': { // Style for the input
            padding: (props) => (props.padding ? props.padding : '10.5px 15px !important'),
        }
    },
    dynamicStyle: (props) => ({
        backgroundColor: theme.palette.primary.main,
        borderRadius: theme.shape.borderRadius,
        width: '100%',
        ...props.style,
    }),
}));

// Different customization options when needed can be found here: https://reactdatepicker.com/
// For example showIcon prop can be passed to show the calendar icon

/**
 * Date picker component with typeable input field
 * @param {object} props 
 * @param {string} props.value - Date value
 * @param {function} props.onChange - Function to handle the change event
 * @param {string} props.className - Classname for the component
 * @param {boolean} props.asObject - Return the date as an object
 * @param {function} props.validate - Function to validate the date
 * @param {string} props.defaultValue - Default value for the date picker
 * @param {string} props.dateFormat - Date format
 * @param {string} props.outputFormat - Output format
 * @param {boolean} props.todayButton - Show the "today" button (its text comes from datetime.today)
 * @param {string} props.iconcolor - Icon color
 * @param {object} props.inputProps - Input props
 * @param {node} props.startAdornment - Start adornment
 * @param {boolean} props.iconVisible - Icon visibility
 * @returns 
 */
function DatePickerTypeable(props) {
    const {
        value,
        onChange = () => { },
        className = {},
        asObject = false,
        validate = () => true,
        defaultValue = null,
        dateFormat = 'dd.MM.yyyy',
        outputFormat,
        todayButton = null,
        iconcolor = 'black',
        inputProps = {},
        startAdornment = null,
        endAdornment = null,
        iconVisible = true,
        allowEmpty = true,
        showWeekNumbers = false,
        ...other
    } = props;

    // These two are composed with the "today button keeps the calendar open" handling
    // below; everything else in `other` goes straight to react-datepicker.
    const {
        shouldCloseOnSelect: closeOnSelect = true,
        onSelect: callerOnSelect,
        ...passThrough
    } = other;

    const classes = useStyles({ ...props, disabled: props.disabled });

    const theme = useTheme();

    const { t } = useTranslation('common');

    const todayButtonText = t('datetime.today');

    // Legend for the "today" ring (react-datepicker_changes.css): the same glyph in
    // front of the today button when there is one, otherwise a small line under the
    // calendar. Today's cell also carries the text as a hover title.
    const todayMarker = (
        <span className="lix-datepicker-today-marker" aria-hidden="true" />
    );
    const renderDayContents = (dayOfMonth, dayDate) =>
        isToday(dayDate) ? (
            <span className="lix-datepicker-today-content" title={todayButtonText}>
                {dayOfMonth}
            </span>
        ) : (
            dayOfMonth
        );

    // The today button selects today WITHOUT closing the calendar; a day click still
    // closes it. react-datepicker closes on every selection (shouldCloseOnSelect), so
    // the library is told never to close, and every selection that did not come from
    // the today button is closed here the way the library would: focus is sent back
    // to the input first (under its preventFocus guard, so the refocus cannot reopen
    // the popup), then the popup closes. Both are methods of the picker instance.
    const pickerRef = useRef(null);
    const handleSelect = (selectedDate, event) => {
        if (callerOnSelect) callerOnSelect(selectedDate, event);
        const picker = pickerRef.current;
        if (!closeOnSelect || passThrough.showTimeSelect || !picker) return;
        const fromTodayButton = !!event?.target?.closest?.(
            '.react-datepicker__today-button'
        );
        if (fromTodayButton) return;
        if (typeof picker.sendFocusBackToInput === 'function') {
            picker.sendFocusBackToInput();
        }
        picker.setOpen(false);
    };

    const getFormat = () => {
        const locale = getDefaultLocale();
        return locale === 'en' ? 'MM/dd/yyyy' : dateFormat;
    }

    const newFormat = getFormat();

    // Given value can be a string, Date object, moment object or dayjs object
    // This function converts the given value to a Date object
    const convertToDate = (value) => {
        if (!value) return null;
        if (typeof value === 'string') {
            return new Date(value);
        }

        if (dayjs.isDayjs(value)) {
            return value.toDate();
        } else if (moment.isMoment && moment.isMoment(value)) {
            return value.toDate();
        } else if (isValid(value)) {
            return value;
        } else {
            return parse(defaultValue, newFormat, new Date());
        }
    };

    const handleOnChange = (date) => {
        if (!validate(date) || !isValid(date)) {
            return;
        }
        if (outputFormat) {
            const formattedDate = format(date, outputFormat);
            onChange(asObject ? { date: date, formatted: formattedDate } : formattedDate);
            return;
        }
        onChange(date);
    }

    const date = convertToDate(value) || (allowEmpty ? null : convertToDate(defaultValue));

    return (
        <DatePicker
            ref={pickerRef}
            className={clsx(classes.dynamicStyle, className)}
            showMonthDropdown={props.showMonthDropdown || true} // Enables the month dropdown
            showYearDropdown={props.showYearDropdown || true}  // Enables the year dropdown
            dropdownMode="select" // Makes the dropdown a select box
            selected={date}
            onChange={(date) => handleOnChange(date)}
            onBlur={props.onBlur}
            dateFormat={newFormat}
            todayButton={
                todayButton ? (
                    <>
                        {todayMarker}
                        {todayButtonText}
                    </>
                ) : null
            }
            renderDayContents={renderDayContents}
            showWeekNumbers={showWeekNumbers}
            customInput={
                <TextInput
                    id="date-picker-input"
                    label={props.label}
                    startAdornment={startAdornment}
                    endAdornment={
                        endAdornment ?
                            endAdornment :
                            iconVisible ?
                                <IconButton
                                    className={classes.icon}
                                    disabled={props.disabled}
                                >
                                    <Icon
                                        path={mdiCalendarBlank}
                                        size={0.7}
                                        color={iconcolor}
                                    />
                                </IconButton>
                                : null
                    }
                    inputProps={
                        {
                            ...inputProps,
                            style: {
                                ...inputProps.style,
                            },
                        }
                    }
                />
            }
            {...passThrough}
            shouldCloseOnSelect={false}
            onSelect={handleSelect}
        >
            {!todayButton && (
                <div className="lix-datepicker-today-legend">
                    {todayMarker}
                    {todayButtonText}
                </div>
            )}
        </DatePicker>
    );

}

export default memo(DatePickerTypeable);